import { ENV } from "../utils";

export class Inspection {
  baseApi = ENV.BASE_API;

  async getTemplates(accessToken, params = {}) {
    const qs = new URLSearchParams();
    if (params.active    !== undefined) qs.set("active",    params.active);
    if (params.site)                    qs.set("site",      params.site);
    if (params.industry)                qs.set("industry",  params.industry);
    const url = `${this.baseApi}/checklist-templates?${qs}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${accessToken}` } });
    const data = await res.json();
    if (res.status !== 200) throw data;
    return data;
  }

  async getTemplate(accessToken, id) {
    const res = await fetch(`${this.baseApi}/checklist-template/${id}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const data = await res.json();
    if (res.status !== 200) throw data;
    return data;
  }

  async createTemplate(accessToken, body) {
    const res = await fetch(`${this.baseApi}/add-checklist-template`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (res.status !== 200) throw data;
    return data;
  }

  async updateTemplate(accessToken, id, body) {
    const res = await fetch(`${this.baseApi}/update-checklist-template/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (res.status !== 200) throw data;
    return data;
  }

  async deleteTemplate(accessToken, id) {
    const res = await fetch(`${this.baseApi}/delete-checklist-template/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const data = await res.json();
    if (res.status !== 200) throw data;
    return data;
  }

  async getInspections(accessToken, params = {}) {
    const qs = new URLSearchParams();
    ["site", "template", "inspector", "status", "year", "company"].forEach((k) => {
      if (params[k] !== undefined) qs.set(k, params[k]);
    });
    const res = await fetch(`${this.baseApi}/inspections?${qs}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const data = await res.json();
    if (res.status !== 200) throw data;
    return data;
  }

  async getInspection(accessToken, id) {
    const res = await fetch(`${this.baseApi}/inspection/${id}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const data = await res.json();
    if (res.status !== 200) throw data;
    return data;
  }

  async createInspection(accessToken, body) {
    const res = await fetch(`${this.baseApi}/add-inspection`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (res.status !== 200) throw data;
    return data;
  }

  async updateInspection(accessToken, id, body) {
    const res = await fetch(`${this.baseApi}/update-inspection/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (res.status !== 200) throw data;
    return data;
  }

  async cancelInspection(accessToken, id, reason) {
    const res = await fetch(`${this.baseApi}/cancel-inspection/${id}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify({ reason }),
    });
    const data = await res.json();
    if (res.status !== 200) throw data;
    return data;
  }

  async deleteInspection(accessToken, id) {
    const res = await fetch(`${this.baseApi}/delete-inspection/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const data = await res.json();
    if (res.status !== 200) throw data;
    return data;
  }

  // Cierre de un hallazgo puntual — foto "después" + responsable, con
  // reevaluación automática del estado del registro en el backend.
  async closeHallazgo(accessToken, inspectionId, hallazgoId, { photos, fixByName }) {
    const res = await fetch(`${this.baseApi}/inspection/${inspectionId}/hallazgo/${hallazgoId}/close`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify({ photos, fixByName }),
    });
    const data = await res.json();
    if (res.status !== 200) throw data;
    return data;
  }

  async uploadFile(accessToken, inspectionId, file) {
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch(`${this.baseApi}/inspection/${inspectionId}/upload-file`, {
      method: "POST",
      headers: { Authorization: `Bearer ${accessToken}` },
      body: fd,
    });
    const data = await res.json();
    if (res.status !== 200) throw data;
    return data; // { key, name }
  }

  getFileUrl(key) {
    return `${this.baseApi}/inspection-file/${key}`;
  }

  // El endpoint requiere header Authorization, así que no puede usarse
  // directo como src de <img>/<a> (el navegador no manda headers custom
  // en esas peticiones). Se trae como blob autenticado por fetch y el
  // caller arma un object URL con URL.createObjectURL(blob).
  async getFileBlob(accessToken, key) {
    const res = await fetch(this.getFileUrl(key), {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!res.ok) throw await res.json().catch(() => ({ msg: "Error al obtener el archivo" }));
    return res.blob();
  }

  async getDashboard(accessToken, params = {}) {
    const qs = new URLSearchParams();
    if (params.year)     qs.set("year",     params.year);
    if (params.site)     qs.set("site",     params.site);
    if (params.template) qs.set("template", params.template);
    const res = await fetch(`${this.baseApi}/inspections/dashboard?${qs}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const data = await res.json();
    if (res.status !== 200) throw data;
    return data;
  }

  // Industria asignada a un sitio a los fines del Checklist (no es la
  // industria de Company/Site — es propia de este módulo, ver
  // server/models/checklistSiteProfile.js).
  async getSiteIndustryProfile(accessToken, siteId) {
    const res = await fetch(`${this.baseApi}/checklist-site-profile/${siteId}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const data = await res.json();
    if (res.status !== 200) throw data;
    return data; // null si el sitio todavía no tiene industria asignada
  }

  async setSiteIndustryProfile(accessToken, siteId, industry) {
    const res = await fetch(`${this.baseApi}/checklist-site-profile/${siteId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify({ industry }),
    });
    const data = await res.json();
    if (res.status !== 200) throw data;
    return data;
  }
}
